import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { roles } from '../utils/mockData';
import { Plus, Edit, Trash2, X, AlertCircle, Loader, CircleMinus, CirclePlus} from 'lucide-react';
import { useToast } from "../components/ui/toast-provider";

// Servicios
import { EmpleadoService } from '../services/empleadoService';

// Tipos de Response
import { EmpleadoResponse } from '../types/empleado';

// Modales
import CrearEmpleado from '../modals/CrearEmpleado';

import LogoBlanco from '../components/img/LogoBlanco.png';

export function Empleados() {
  const [empleados, setEmpleados] = useState<EmpleadoResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [empleadoEditando, setEmpleadoEditando] = useState<number>(0);
  const [filtroActivo, setFiltroActivo] = useState(true);
  const { addToast } = useToast();

  // Cargar empleados al montar el componente y cada vez que cambie el filtro
  useEffect(() => {
    cargarEmpleados();
  }, [filtroActivo]);

  useEffect(() => {
  }, [empleadoEditando]);

  const cargarEmpleados = async () => {
    try {
      setIsLoading(true);
      const res = await EmpleadoService.getAll(filtroActivo);
      setEmpleados(res.data);
    } catch (err) {
      setError("Error al cargar empleados");
    } finally {
      setIsLoading(false);
    }
  };

  const cambiarEstado = async (id: number, activo: boolean) => {
    try {
      setIsLoading(true);
      if (activo) {
        await EmpleadoService.desactivar(id);
        addToast("Empleado desactivado exitosamente");
      } else {
        await EmpleadoService.activar(id);
        addToast("Empleado activado exitosamente");
      }
      await cargarEmpleados();
    } catch (err) {
      setError("Error al cambiar estado del empleado");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-6 md:grid-cols-6 gap-4 p-6 text-white bg-primary border border-primary rounded-t-lg items-center">
        {/* Logo */}
        <div className="col-span-2 md:col-span-1 flex justify-center md:justify-start">
            <img 
            src={LogoBlanco} 
            alt="Logo AGEMI" 
            className="w-20 md:w-40 h-auto object-contain"
            />
        </div>
        <div className="col-span-4 md:col-span-5 flex flex-col text-sm md:text-base">
          <h1 className="text-3xl font-bold">Empleados AGEMI</h1>
        <p className="text-base text-white mt-1 md:mt-2">
          Gestión de los empleados del sistema
        </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <Select
              value={filtroActivo ? '1' : '0'}
              onChange={(e) => setFiltroActivo(e.target.value === '1')}
              options={[
                { value: '1', label: 'Empleados Activos' },
                { value: '0', label: 'Empleados Inactivos' },
              ]}
              className="w-full md:w-auto bg-white text-primary"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-end mb-4 gap-2">
            {!mostrarFormulario && (
              <Button onClick={() => setMostrarFormulario(true)} variant="accent" className="w-full md:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Empleado
              </Button>
            )}
          </div>
          {error && (
            <div className="p-4 mb-4 bg-destructive/10 border border-destructive rounded-lg text-destructive flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Error al cargar empleados</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary mr-2" />
              <p>Cargando empleados...</p>
            </div>
          ) : empleados.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No hay empleados para mostrar</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[400px] border-gray-400 mb-10">
                <table className="min-w-full text-left">
                  <thead className="bg-primary text-white sticky top-0">
                    <tr>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Nombre</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Teléfono</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold hidden sm:table-cell">Correo</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold hidden md:table-cell">DPI</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold hidden lg:table-cell">Puesto</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empleados.map((empleado) => (
                      <tr key={empleado.idEmpleado} className="border-t border-border hover:bg-muted/30 transition-colors">
                        <td className="px-3 md:px-4 py-2 md:py-3 font-medium text-xs md:text-sm">
                          <div className="truncate">{empleado.nombreCompleto}</div>
                        </td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">{empleado.telefono}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm hidden sm:table-cell truncate">{empleado.correo}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm hidden md:table-cell">{empleado.dpi}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm hidden lg:table-cell">{empleado.puesto}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3">
                          <div className="flex justify-center gap-1 md:gap-2">
                            <Button
                              size="sm"
                              variant="accent"
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                setEmpleadoEditando(Number(empleado.idEmpleado));
                                setMostrarFormulario(true);
                              }}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant={filtroActivo ? "destructive" : "primary"}
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                cambiarEstado(Number(empleado.idEmpleado), filtroActivo);
                              }}
                            >
                              {filtroActivo ? <CircleMinus size={16} /> : <CirclePlus size={16} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CrearEmpleado
        isOpen={mostrarFormulario} 
        onClose={async () => {
          setMostrarFormulario(false);
          setEmpleadoEditando(0);
        }}
        onGuardado={async() => {
          await cargarEmpleados();
          setMostrarFormulario(false);
        }}
        idEditando = {empleadoEditando !== null ? Number(empleadoEditando) : 0}
      />      
    </div>
  );
}
